import { useEffect, useState } from "react";

import { PlusSmallIcon } from "@heroicons/react/24/outline";

import DepollutionStepModal from "../../components/depollution/DepollutionStepModal";
import DepollutionStepsTable from "../../components/depollution/DepollutionStepsTable";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDepollution from "../../data/useDepollution";
import { IDepollutionStep } from "../../models/Depollution";

export default function DepollutionEditor() {
  const { setInfo } = usePageNameContext();
  const depollutionLabel = "Depollution";
  const { allDepollutionSteps, updateDepollutionStepOrder } = useDepollution(undefined, undefined, true);
  const [depollutionSteps, setDepollutionSteps] = useState<IDepollutionStep[] | undefined>(undefined);
  const [editDepollutionStep, setEditDepollutionStep] = useState<IDepollutionStep | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    setInfo({
      name: `${depollutionLabel} Editor`,
      desc: `View and manage vehicle ${depollutionLabel.toLowerCase()} steps`,
    });
  }, [setInfo, depollutionLabel]);

  useEffect(() => {
    if (!allDepollutionSteps.isLoading && allDepollutionSteps.data) {
      const orderedSteps = [...allDepollutionSteps.data.data].sort((a, b) => a.order - b.order);
      setDepollutionSteps(orderedSteps);
      setIsLoading(false);
    }
  }, [allDepollutionSteps.isLoading, allDepollutionSteps.data]);

  const handleSetDepollutionStep = (depollutionStep: IDepollutionStep) => {
    setEditDepollutionStep(depollutionStep);
    setCreateOpen(true);
  };

  const handleAddDepollutionStep = () => {
    setEditDepollutionStep(undefined);
    setCreateOpen(true);
  };

  const handleMoveDepollutionStep = async (stepId: string, direction: "up" | "down") => {
    if (!depollutionSteps || isReordering) return;

    const orderedSteps = [...depollutionSteps].sort((a, b) => a.order - b.order);
    const index = orderedSteps.findIndex((x) => x.id === stepId);

    if (index < 0) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= orderedSteps.length) return;

    setIsReordering(true);

    try {
      await new Promise<void>((resolve, reject) => {
        updateDepollutionStepOrder.mutate(
          { depollutionStepId: stepId, newIndex: targetIndex },
          {
            onSuccess: () => resolve(),
            onError: reject,
          }
        );
      });

      await allDepollutionSteps.refetch();
    } catch {
      await allDepollutionSteps.refetch();
    } finally {
      setIsReordering(false);
    }
  };

  const nextOrder = (depollutionSteps?.length ?? 0) + 1;

  return allDepollutionSteps.isLoading || isLoading ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : (
    <>
      <DepollutionStepModal
        open={createOpen}
        setOpen={setCreateOpen}
        depollutionStep={editDepollutionStep}
        defaultOrder={nextOrder}
      />
      <div className="mt-4 flex justify-between">
        <button
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
          type="button"
          onClick={handleAddDepollutionStep}
        >
          <PlusSmallIcon className="h-5 w-5 mr-2" />
          Add New {depollutionLabel} Step
        </button>
      </div>

      <DepollutionStepsTable
        depollutionSteps={depollutionSteps!}
        setDepollutionStep={handleSetDepollutionStep}
        moveDepollutionStep={handleMoveDepollutionStep}
        isReordering={isReordering}
      />
    </>
  );
}
